// cSpell:ignore xyflow Silae SILAE
import type { ReactElement } from "react";
import { Trans, useTranslation } from "react-i18next";

import { useSilaeFormatters } from "@/hooks/useSilaeFormatters";
import type { SilaeCondition } from "@/types/silae";
import { logicalOperatorLabel } from "../../utils/logicalOperatorLabel";
import { RichFormattedText } from "../../utils/RichFormattedText";

interface ConditionsListBlockProps {
  conditions: SilaeCondition[];
  conditionsOperators: string[];
  /** Clé i18n du paragraphe introductif (peut contenir des balises <main/>, <b/>, etc.). */
  descriptionKey: string;
  /** Composants de remplacement pour les balises présentes dans la chaîne i18n. */
  descriptionComponents?: Record<string, ReactElement>;
  /** Valeurs interpolées dans la chaîne i18n (ex: `{{bonus}}`). */
  descriptionValues?: Record<string, string>;
  /** Couleur du libellé d'opérateur (ET/OU) entre deux conditions. */
  operatorClassName?: string;
}

/**
 * Bloc partagé "description + liste numérotée de conditions" — utilisé par
 * `ConditionsNode` (flux principal) et `BonusConditionsNode` (conditions
 * propres à une prime). Aucun style spécifique au type de nœud n'est porté
 * ici : seul le contenu logique est mutualisé.
 */
export function ConditionsListBlock({
  conditions,
  conditionsOperators,
  descriptionKey,
  descriptionComponents,
  descriptionValues,
  operatorClassName = "text-amber-700 dark:text-amber-400",
}: ConditionsListBlockProps) {
  const { t } = useTranslation();
  const { formatConditionDescription } = useSilaeFormatters();

  return (
    <>
      <p className="mb-3 text-[11px] leading-relaxed text-zinc-600 dark:text-zinc-300">
        <Trans
          i18nKey={descriptionKey}
          values={descriptionValues}
          components={descriptionComponents}
        />
      </p>
      <ul className="space-y-1.5">
        {conditions.map((cond, i) => (
          <li key={cond.id} className="flex flex-col items-stretch gap-1">
            <div className="flex gap-2 rounded-md border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 px-2 py-1.5 wrap-break-word">
              <span className="font-mono text-[10px] text-zinc-500 dark:text-zinc-400 mt-0.5 shrink-0">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-zinc-900 dark:text-zinc-50">
                <RichFormattedText text={formatConditionDescription(cond)} />
              </span>
            </div>
            {i < conditions.length - 1 && conditions.length > 1 && (
              <span
                className={`self-center text-[10px] font-bold tracking-wider ${operatorClassName}`}
              >
                {logicalOperatorLabel(conditionsOperators[i], t)}
              </span>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}
